/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * One line saying what this client is after.
 *
 * The list row used to carry a one-word badge for the kind of client and a
 * relative date underneath — and since most records have never had a contact
 * logged, that date was an em dash on nearly every row. A list of names with
 * a dash under each is a list that has told you nothing, and it reads as
 * broken rather than as empty.
 *
 * What the form actually collects is a demand: buy or rent, which kind of
 * property, in which city. That is the thing a broker scanning the list
 * wants, it distinguishes one row from the next, and it is already in the
 * list payload — no extra request, and no new field.
 *
 * Owning listings is the other half. A client with listings of their own is
 * described by how many, since the count is what decides how much of the
 * agency's stock depends on that one relationship.
 */

/** The parts of a list row this summary reads. */
export interface ClientSummaryInputs {
  /** The stored demand profile, as mapped onto the row. */
  preferences?: Record<string, unknown>;
  /** How many listings the client owns, when the list mapped it through. */
  owned_properties_count?: number;
  /** Last resort, so a row is never blank. */
  phone?: string;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The API says `rental` where the form's labels say `rent`.
 *
 * Mapped rather than renamed: the wire format is not ours to change from
 * here, and a label map that silently misses a key renders the key itself.
 */
const BUSINESS_TYPE_KEYS: Record<string, string> = {
  sale: 'sale',
  buy: 'buy',
  rental: 'rent',
  rent: 'rent',
  both: 'both',
  exchange: 'exchange',
};

/** Reads a non-empty string out of an untyped profile field. */
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Reads the first entry of an untyped list of strings. */
function firstOf(value: unknown): string | undefined {
  return Array.isArray(value) ? text(value[0]) : undefined;
}

/**
 * Builds the intent line ("Comprar · Studio · São Paulo") from the demand
 * profile, or an empty string when the profile carries no business type.
 *
 * @param profile - The stored demand profile.
 * @param t - Scoped to `features/clients`.
 * @returns The joined intent line, or `''`.
 */
function describeIntent(profile: Record<string, unknown>, t: TFn): string {
  const businessKey = BUSINESS_TYPE_KEYS[text(profile.business_type) ?? ''];
  if (!businessKey) return '';

  /* Kind and city qualify the intent, so they only ever follow it: "Studio ·
     São Paulo" with no verb in front reads as an address. */
  const parts = [
    t(`form.businessTypes.${businessKey}`),
    firstOf(profile.property_kinds),
    firstOf(profile.cities),
  ].filter(Boolean) as string[];

  return parts.join(' · ');
}

/**
 * Builds the row's second line.
 *
 * Returns an empty string when there is genuinely nothing to say, so the
 * caller decides what an empty row looks like rather than being handed a
 * placeholder it has to recognise.
 *
 * @param client - The list row.
 * @param t - Scoped to `features/clients`.
 * @returns The line, or `''` when the record carries nothing to describe.
 */
export function describeClient(client: ClientSummaryInputs, t: TFn): string {
  const owned = client.owned_properties_count ?? 0;
  if (owned > 0) return t('list.summary.owned', { count: owned });

  const intent = describeIntent(client.preferences ?? {}, t);
  if (intent) return intent;

  return client.phone?.trim() || '';
}
